import React from 'react';
import { Section, Button, FadeIn, SEO } from '../components/UI';
import { Award, BookOpen, Heart, Coffee, GraduationCap, ShieldCheck } from 'lucide-react';

export default function About() {
  return (
    <div className="pt-20">
      <SEO 
        title="Om mig" 
        description="Mød mennesket bag Sorgskab. Jeg er dedikeret til at støtte dig gennem livets sværeste overgange med nærvær og faglighed."
      />
      
      {/* Hero Section */}
      <Section className="bg-transparent pt-8 md:pt-12 pb-12 md:pb-24 relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center relative z-10">
          <div className="lg:col-span-5">
            <FadeIn className="pt-12 md:pt-0">
              <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-6 block">Mennesket bag</span>
              <h1 className="text-4xl md:text-7xl font-serif text-sorg-dark mb-8 leading-[1.1]">
                Om mig
              </h1>
              <p className="text-xl text-sorg-dark/70 leading-relaxed mb-10">
                Jeg tror på, at ingen skal bære deres sorg alene. Mit ønske er at skabe et trygt fundament, hvor du kan finde ro til at hele og mod til at leve videre.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button to="/kontakt" className="px-6 py-2.5 md:px-8 md:py-3">Kontakt mig</Button>
              </div>
            </FadeIn>
          </div>
          
          <div className="lg:col-span-7">
            <FadeIn direction="left" delay={0.2}>
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-sorg-beige/50 organic-shape -z-10"></div>
                <img 
                  src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/a998fb17-db19-44bb-887a-eaf6e083a1e7.png" 
                  alt="Portræt af vejlederen" 
                  className="w-full h-auto object-contain rounded-2xl soft-shadow"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-sorg-sage/10 organic-shape -z-10"></div>
                <div className="absolute -bottom-6 md:left-0 lg:-left-6 bg-white/80 backdrop-blur-md p-6 rounded-2xl soft-shadow border border-white/50 max-w-[200px] hidden md:block">
                  <p className="text-sm font-serif text-sorg-dark italic">"Nærvær er den største gave, vi kan give hinanden."</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Story Section */}
      <Section id="min-historie" glass>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 relative z-10">
          <div className="lg:col-span-2 space-y-8 text-sorg-dark/70 text-lg leading-relaxed">
            <FadeIn>
              <h2 className="text-3xl font-serif text-sorg-dark mb-6">Min vej ind i arbejdet med sorg</h2>
              
              <h3 className="text-xl font-serif text-sorg-earth mt-8 mb-3">Et sprog for det svære</h3>
              <p className="mb-6">
                Min interesse for arbejdet med sorg og livets afslutning udspringer af et ønske om at skabe et varmt og trygt sprog for det, der ellers kan føles så uoverskueligt og ensomt. Jeg tror på, at når vi tør dele mørket og tale åbent om vores tab, kan vi mindske den isolation, som sorgen ofte bringer med sig, og i stedet finde en dyb og helende forbindelse til hinanden.
              </p>

              <h3 className="text-xl font-serif text-sorg-earth mt-8 mb-3">Erfaring fra livets mange facetter</h3>
              <p className="mb-6">
                Som uddannet pædagog har jeg altid haft et hjerte for det hele menneske. Min vej har ført mig igennem mange af livets sårbare kroge – fra mine år som tjener og den nære omsorg i hjemmeplejen til arbejdet hos Røde Kors på asylcentre, pædagog i et gadeteam og senest rollen som begravelsesrådgiver. Gennem mange år har jeg også fundet dyb mening i at være frivillig på Hospice, leder i vågetjenesten og en lyttende stemme på Livslinien. Disse møder har lært mig at være til stede i det, der gør ondt, med en dyb respekt for den sårbarhed og styrke, vi hver især bærer.
              </p>

              <h3 className="text-xl font-serif text-sorg-earth mt-8 mb-3">En holistisk og nærværende støtte</h3>
              <p className="mb-6">
                For at kunne møde dig med den bedst mulige støtte, er jeg uddannet som certificeret sorgvejleder, har taget kurser i dødsdoulaens rolle og er i gang med en ayurvedisk uddannelse for at kunne forbinde krop, sind og sjæl i en holistisk helhed. Jeg er her ikke for at fjerne eller "fikse" din sorg, men for at gå nænsomt ved siden af dig med min ro, min erfaring og mit fulde nærvær, mens du finder din egen vej i dit eget tempo.
              </p>
            </FadeIn>

            <FadeIn delay={0.2}>
              <h3 className="text-2xl font-serif text-sorg-dark mb-4">Min filosofi</h3>
              <p>
                Jeg arbejder ud fra et helhedsorienteret menneskesyn. Det betyder, at jeg ser på både de mentale, kropslige og eksistentielle aspekter af sorgen. Hvert menneske er unikt, og derfor er hver samtale og hvert forløb tilpasset netop dine behov og din situation.
              </p>
            </FadeIn>
          </div>

          <div className="lg:col-span-1">
            <FadeIn direction="left" className="bg-sorg-beige/20 p-10 rounded-3xl border border-sorg-beige sticky top-32">
              <h3 className="text-2xl font-serif text-sorg-dark mb-6">Uddannelse & Erfaring</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-sorg-earth flex-shrink-0 mt-0.5" strokeWidth={1} />
                  <span>Certificeret Sorgvejleder</span>
                </li>
                <li className="flex items-start space-x-3">
                  <BookOpen className="w-5 h-5 text-sorg-earth flex-shrink-0 mt-0.5" strokeWidth={1} />
                  <span>Kurser i dødsdoulaens rolle</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Heart className="w-5 h-5 text-sorg-earth flex-shrink-0 mt-0.5" strokeWidth={1} />
                  <span>I gang med ayurvedisk uddannelse (holistisk helhed)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <GraduationCap className="w-5 h-5 text-sorg-earth flex-shrink-0 mt-0.5" strokeWidth={1} />
                  <span>Uddannet pædagog</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Coffee className="w-5 h-5 text-sorg-earth flex-shrink-0 mt-0.5" strokeWidth={1} />
                  <span>Mangeårig frivillig på Hospice, leder i vågetjenesten og rådgiver på Livslinien</span>
                </li>
                <li className="flex items-start space-x-3">
                  <ShieldCheck className="w-5 h-5 text-sorg-earth flex-shrink-0 mt-0.5" strokeWidth={1} />
                  <span>Erfaring som tjener, i hjemmeplejen, hos Røde Kors på asylcentre, pædagog i et gadeteam og nu som begravelsesrådgiver</span>
                </li>
              </ul>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Values Section */}
      <Section className="bg-sorg-sand/40 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <FadeIn delay={0.1} className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-white/50 flex items-center justify-center text-sorg-earth mb-6 border border-white/50 shadow-sm">
                <GraduationCap size={32} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-2xl mb-4 text-sorg-earth">Faglighed</h4>
              <p className="text-sm text-sorg-dark/60">Jeg holder mig løbende opdateret med den nyeste viden inden for sorgforskning.</p>
            </FadeIn>
            <FadeIn delay={0.2} className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-white/50 flex items-center justify-center text-sorg-earth mb-6 border border-white/50 shadow-sm">
                <Heart size={32} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-2xl mb-4 text-sorg-earth">Empati</h4>
              <p className="text-sm text-sorg-dark/60">Jeg møder dig med et åbent hjerte og en dyb forståelse for din situation.</p>
            </FadeIn>
            <FadeIn delay={0.3} className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-white/50 flex items-center justify-center text-sorg-earth mb-6 border border-white/50 shadow-sm">
                <ShieldCheck size={32} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-2xl mb-4 text-sorg-earth">Fortrolighed</h4>
              <p className="text-sm text-sorg-dark/60">Dine historier er trygge hos mig. Jeg overholder fuld tavshedspligt.</p>
            </FadeIn>
          </div>
        </div>
      </Section>
    </div>
  );
}
